// 新增 Kinio 設備（plan/vms-web-v0.md DEV-02；provisioning §5）。web 建 pairing session，依設備支援的方式交出配對碼：
//   ② 有線 LAN / NVR / AI box（method=lan）：顯示配對碼與兌換位址，在設備本機 UI 輸入；
//   ① 攝影機鏡頭掃（method=qr）：web 顯示 KWF:1 QR（Wi-Fi + 配對碼，provisioning §4.6）；
//   ③ 交給手機 App（method=ble）：web 顯示 https://<portal>/pair#… QR，App 掃了再掃標籤、走 BLE / SoftAP（provisioning §5.1）。
// 之後輪詢到 bound。web 不經手設備私鑰（設備自己拿配對碼去 pair_url 兌換憑證）。
// Kinio Station（AID 型態 KST）只開放給指定品牌（SPEC #catalog-kinio-family-brands）：品牌沒開放時開 session 前就擋；
//   Kinio Node（GEN）配對時看不出機種，由平台回 422 MODEL_NOT_AVAILABLE_FOR_BRAND。
// ① / ③ 的 QR：按了才顯示；bound / 過期 / 顯示滿 5 分鐘自動隱藏；Wi-Fi 密碼只在這個元件的記憶體，不送平台、不存、不 log（Eddy 2026-09-27）。
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Dialog } from '../../components/Dialog';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { browserTz, validTz } from '../../lib/tz';
import { looksLikeAid, pairingApi, type PairingSession } from '../../lib/deviceApi';
import { blockKinioFamilyAid } from '../../lib/kinioFamily';
import { deviceHref } from './DeviceDetailPage';
import { buildKwf, buildPairLink, checkWifi, posixTz } from './pairQr';
import { PairQrCode } from './PairQrCode';
import { PasswordInput } from '../../components/PasswordInput';
import { deviceApi } from '../../lib/deviceApi';
import { aidKind, defaultDeviceName, DEVICE_NAME_MAX, nameBase } from '../../lib/pairName';
import { allows } from '../../lib/myDevices';
import { kinioFetch, planeBase } from '@kinio/web-sdk';

type Method = 'lan' | 'qr' | 'app';
const QR_TTL_MS = 5 * 60_000;
const apiMethod: Record<Method, 'lan' | 'qr' | 'ble'> = { lan: 'lan', qr: 'qr', app: 'ble' };

/** ① 的 z：家戶有時區設定就用它（ORG-02；家戶沒設時「我的設備」帶錨點場域的），否則瀏覽器時區（Eddy 2026-09-27）。 */
export function deviceTz(home: unknown): string {
  const raw = (home as { timezone?: string } | undefined)?.timezone;
  return validTz(raw) ? raw : browserTz();
}

/** 設備庫存〔配對新設備〕的目標（SPEC #org-tree-scope OT34、product-4-1）：結構樹上的家戶（含未指派的），不需要資料權。 */
export interface PairTarget {
  home_id: string;
  /** 「北區 › 信義據點 › 301」 */
  label: string;
  api: string;
  timezone?: string;
  unassigned?: boolean;
  /** 家戶直屬的節點（設備庫存預先指派要帶） */
  node_id?: string;
  /** 從根到家戶的節點 id（篩「這個節點子樹底下的家戶」用） */
  node_path?: string[];
}

export function PairDialog({ open, close, targets, initialAid }: { open: boolean; close(): void; targets?: PairTarget[]; /** 偵測到的設備〔加入〕帶進來（DEV-16） */ initialAid?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { config } = useApp();
  const portalHost = config.portal_host ?? '';
  const scope = useScope();
  const own = scope.visible.filter((h) => h.role === 'owner' || h.role === 'admin');
  const homes = targets
    ? targets.map((x) => ({ home_id: x.home_id, display_name: x.label + (x.unassigned ? ` (${t('ot.unassigned')})` : ''), endpoints: { api: x.api }, timezone: x.timezone }))
    : own;
  /** 庫存配對：裝機的人不因此得到資料權——這一戶沒有任何一台我能看即時（media.live）= 沒有影像權 */
  const noAccess = (homeId: string) => !!targets && !scope.devices.some((d) => d.home_id === homeId && allows(d, 'media.live'));
  const [homeId, setHomeId] = useState('');
  const [aid, setAid] = useState('');
  const [method, setMethod] = useState<Method>('lan');
  const [ssid, setSsid] = useState('');
  const [psk, setPsk] = useState('');
  const [sess, setSess] = useState<PairingSession | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [qrAt, setQrAt] = useState<number | null>(null); // 按「顯示 QR」的時間；null = 隱藏
  const [named, setNamed] = useState<string | null>(null); // DEV-02：bound 之後的取名步驟做完（送出或略過）時寫入的名稱
  const home = homes.find((h) => h.home_id === (homeId || homes[0]?.home_id));
  const api = home?.endpoints.api ?? '';
  const live = !!sess && ['pending', 'connected'].includes(sess.status);

  useEffect(() => {
    if (open && initialAid) setAid(initialAid);
  }, [open, initialAid]);

  useEffect(() => {
    if (!open) {
      setSess(null);
      setErr(null);
      setAid('');
      setSsid('');
      setPsk('');
      setQrAt(null);
      setNamed(null);
    }
  }, [open]);

  // session 結束（bound / failed / 過期…）：QR 收起來、Wi-Fi 密碼清掉
  useEffect(() => {
    if (sess && !live) {
      setQrAt(null);
      setPsk('');
    }
  }, [sess, live]);

  // QR 顯示中每秒更新倒數（到 5 分鐘自動隱藏）
  useEffect(() => {
    if (qrAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [qrAt]);

  // 輪詢 session 狀態（2 秒），直到 bound / failed / canceled / expired
  useEffect(() => {
    if (!sess || !live) return;
    const id = setInterval(async () => {
      setNow(Date.now());
      try {
        const s = await pairingApi.get(api, sess.binding_id);
        setSess((cur) => (cur ? { ...cur, ...s, pairing_code: cur.pairing_code, pair_url: cur.pair_url } : cur));
        if (s.status === 'bound') void qc.invalidateQueries({ queryKey: ['home', sess.home_id] });
      } catch {
        /* 下一輪再試 */
      }
    }, 2000);
    return () => clearInterval(id);
  }, [sess, live, api, qc]);

  const start = async () => {
    setErr(null);
    const a = aid.trim().toUpperCase();
    if (!looksLikeAid(a)) {
      setErr('BAD_DEVICE_REF');
      return;
    }
    if (blockKinioFamilyAid(config.brand, a, config.kinio_family)) {
      setErr('MODEL_NOT_AVAILABLE_FOR_BRAND');
      return;
    }
    if (method === 'qr') {
      const bad = checkWifi(ssid, psk);
      if (bad) {
        setErr(bad);
        return;
      }
    }
    if (!home) return;
    setBusy(true);
    try {
      setSess(await pairingApi.start(api, { home_id: home.home_id, device_ref: a, method: apiMethod[method] }));
      setQrAt(null);
      setNamed(null);
    } catch (e) {
      setErr((e as { code?: string }).code ?? String(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    if (live && sess) {
      try {
        await pairingApi.cancel(api, sess.binding_id);
      } catch {
        /* 過期的 session 取消失敗無妨 */
      }
    }
    close();
  };
  const left = sess ? Math.max(0, Math.floor((Date.parse(sess.expires_at) - now) / 1000)) : 0;
  const qrLeft = qrAt === null ? 0 : Math.max(0, Math.floor((qrAt + QR_TTL_MS - now) / 1000));
  const qrVisible = live && left > 0 && qrAt !== null && qrLeft > 0;
  const mmss = (n: number) => ({ m: Math.floor(n / 60), s: String(n % 60).padStart(2, '0') });

  // QR 內容只在顯示時組（不進 state、不 log）
  let qrText = '';
  let qrErr: string | null = null;
  if (qrVisible && sess?.pairing_code && sess.pair_url) {
    try {
      qrText =
        method === 'qr'
          ? buildKwf({ aid: sess.device_ref, ssid, psk, code: sess.pairing_code, pairUrl: sess.pair_url, tz: posixTz(deviceTz(home)) })
          : buildPairLink({ portalHost, bindingId: sess.binding_id, code: sess.pairing_code, aid: sess.device_ref, api, pairUrl: sess.pair_url });
    } catch (e) {
      qrErr = (e as { code?: string }).code ?? 'BAD_SESSION';
    }
  }

  return (
    <Dialog open={open} title={t('pair.title')} onClose={() => void cancel()}>
      {!sess ? (
        <>
          {homes.length === 0 ? (
            <p className="muted">{t('pair.no_home')}</p>
          ) : (
            <>
              <label className="field">
                <span>{t('devices.col_home')}</span>
                <select className="input" value={home?.home_id ?? ''} onChange={(e) => setHomeId(e.target.value)}>
                  {homes.map((h) => (
                    <option key={h.home_id} value={h.home_id}>
                      {h.display_name || h.home_id}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="field">
                <legend>{t('pair.method')}</legend>
                <label>
                  <input type="radio" name="pair-method" checked={method === 'lan'} onChange={() => setMethod('lan')} /> {t('pair.method_lan')}
                </label>
                <label>
                  <input type="radio" name="pair-method" checked={method === 'qr'} onChange={() => setMethod('qr')} /> {t('pair.method_qr')}
                </label>
                <label className={portalHost ? undefined : 'muted'}>
                  <input type="radio" name="pair-method" disabled={!portalHost} checked={method === 'app'} onChange={() => setMethod('app')} /> {t('pair.method_app')}
                  {!portalHost && <span className="small"> {t('pair.method_app_unavailable')}</span>}
                </label>
              </fieldset>
              <label className="field">
                <span>{t('pair.aid_label')}</span>
                <input className="input mono" value={aid} onChange={(e) => setAid(e.target.value)} placeholder="CNV-XXXXXXXXXXXXXXXX-X" aria-label={t('pair.aid_label')} />
                <span className="muted small">{t('pair.aid_hint')}</span>
                {/* 標籤示意圖（P1-8）：設備底部標籤上「設備編號」那一行在哪 */}
                <span className="pair-label-demo" aria-hidden>
                  <span className="pair-label-qr" />
                  <span>
                    <span className="muted small">{t('pair.aid_demo_title')}</span>
                    <br />
                    <span className="mono">CNV-XXXXXXXXXXXXXXXX-X</span>
                  </span>
                </span>
              </label>
              {method === 'qr' && (
                <>
                  <label className="field">
                    <span>{t('pair.wifi_ssid')}</span>
                    <input className="input" value={ssid} onChange={(e) => setSsid(e.target.value)} autoComplete="off" spellCheck={false} aria-label={t('pair.wifi_ssid')} />
                  </label>
                  <label className="field">
                    <span>{t('pair.wifi_psk')}</span>
                    <PasswordInput value={psk} onChange={(e) => setPsk(e.target.value)} autoComplete="off" aria-label={t('pair.wifi_psk')} />
                    <span className="muted small">{t('pair.wifi_note')}</span>
                  </label>
                </>
              )}
              {err && <p className="error">{t(`pair.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
              <div className="dialog-actions">
                <button className="btn" onClick={close}>
                  {t('common.cancel')}
                </button>
                <button className="btn btn-primary" disabled={busy || !aid.trim()} onClick={() => void start()}>
                  {t('pair.start')}
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div data-pairing-status={sess.status} data-pairing-method={method}>
          {sess.status === 'bound' && named === null ? (
            <NameStep
              api={api}
              homeId={sess.home_id}
              aid={sess.device_ref}
              base={(() => {
                const tg = targets?.find((x) => x.home_id === sess.home_id);
                const hn = tg ? (tg.label.split(' › ').pop() ?? '') : (homes.find((h) => h.home_id === sess.home_id)?.display_name ?? '');
                return nameBase(hn, tg?.label);
              })()}
              done={(name) => {
                setNamed(name);
                void qc.invalidateQueries({ queryKey: ['home', sess.home_id] });
                void qc.invalidateQueries({ queryKey: ['me-devices'] });
              }}
            />
          ) : sess.status === 'bound' && noAccess(sess.home_id) ? (
            <>
              <p style={{ color: 'var(--ok)' }} data-testid="pair-joined-no-access">
                {t('ot.pair_joined_no_access')}
              </p>
              <div className="dialog-actions">
                <button className="btn btn-primary" onClick={close}>
                  {t('common.done')}
                </button>
              </div>
            </>
          ) : sess.status === 'bound' ? (
            <>
              <p style={{ color: 'var(--ok)' }}>{t('pair.bound', { aid: named || t('pair.kind_' + aidKind(sess.device_ref)) })}</p>
              <div className="dialog-actions">
                <Link className="btn btn-primary" to={deviceHref({ homeId: sess.home_id, aid: sess.device_ref })} onClick={close}>
                  {t('pair.open_device')}
                </Link>
              </div>
            </>
          ) : !live || left === 0 ? (
            <>
              <p className="error">{t(`pair.status_${left === 0 && sess.status === 'pending' ? 'expired' : sess.status}`)}</p>
              {sess.fail_reason && <p className="muted small">{/^[A-Z_]+$/.test(sess.fail_reason) ? t(`pair.err_${sess.fail_reason}`, { defaultValue: sess.fail_reason }) : sess.fail_reason}</p>}
              <div className="dialog-actions">
                <button className="btn" onClick={() => setSess(null)}>
                  {t('pair.again')}
                </button>
              </div>
            </>
          ) : (
            <>
              {method === 'lan' ? (
                <>
                  <p>{t('pair.enter_on_device')}</p>
                  <div className="pair-code mono" aria-label={t('pair.code')}>
                    {sess.pairing_code}
                  </div>
                  {sess.pair_url && (
                    <p className="muted small">
                      {t('pair.url')}
                      {t('common.colon')}
                      <span className="mono">{sess.pair_url}</span>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>{t(method === 'qr' ? 'pair.scan_with_camera' : 'pair.scan_with_app')}</p>
                  {qrVisible && qrText ? (
                    <>
                      <PairQrCode text={qrText} label={t(method === 'qr' ? 'pair.qr_alt_kwf' : 'pair.qr_alt_app')} />
                      <p className="muted small">{t('pair.qr_auto_hide', mmss(qrLeft))}</p>
                    </>
                  ) : (
                    <>
                      {qrErr && <p className="error">{t(`pair.err_${qrErr}`, { defaultValue: qrErr })}</p>}
                      {qrAt !== null && <p className="muted small">{t('pair.qr_hidden')}</p>}
                      <button className="btn btn-primary" onClick={() => setQrAt(Date.now())}>
                        {t(qrAt === null ? 'pair.show_qr' : 'pair.show_qr_again')}
                      </button>
                    </>
                  )}
                </>
              )}
              <p className="muted">
                {t(`pair.status_${sess.status}`)} · {t('pair.expires_in', mmss(left))}
              </p>
              <div className="dialog-actions">
                {qrVisible && (
                  <button className="btn" onClick={() => setQrAt(null)}>
                    {t('pair.hide_qr')}
                  </button>
                )}
                <button className="btn" onClick={() => void cancel()}>
                  {t('pair.cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** DEV-02 配對完取名：預填「家戶 / 節點 + 機種」，〔儲存名稱〕或〔略過〕都寫入（略過 = 預填值；SPEC 推論、待確認）。 */
function NameStep({ api, homeId, aid, base, done }: { api: string; homeId: string; aid: string; base: string; done(name: string): void }) {
  const { t, i18n } = useTranslation();
  const [initial, setInitial] = useState(() => defaultDeviceName(base, t('pair.kind_' + aidKind(aid))));
  const [name, setName] = useState(initial);
  // 機種友善名以平台的名稱解析為準（component/protocol 的 golden，依語系；SPEC #notify-177）：看得到這台時用「我的設備」回的名稱，
  // 看不到（裝機的人沒有資料權）才用 AID 類型碼的語系化種類名
  useEffect(() => {
    let off = false;
    kinioFetch<{ devices: { device_ref: string; display_name: string }[] }>(
      `${planeBase(api)}/v1/me/devices?home_id=${encodeURIComponent(homeId)}&limit=500`,
      { headers: { 'Accept-Language': i18n.language } },
    )
      .then((r) => {
        const d = r?.devices?.find((x) => x.device_ref === aid);
        if (off || !d?.display_name || d.display_name.includes(aid)) return;
        const v = defaultDeviceName(base, d.display_name);
        setInitial(v);
        setName((cur) => (cur === initial ? v : cur));
      })
      .catch(() => undefined);
    return () => {
      off = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, homeId, aid, base]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const save = async (value: string) => {
    const v = value.trim();
    if (!v || [...v].length > DEVICE_NAME_MAX) {
      setErr(t('pair.err_INVALID_NAME'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await deviceApi.rename(api, homeId, aid, v);
      done(v);
    } catch (e) {
      const x = e as { status?: number; code?: string };
      if (x.status === 403 || x.code === 'FORBIDDEN' || x.code === 'NOT_A_MEMBER') setForbidden(true);
      else setErr(x.code === 'INVALID_NAME' ? t('pair.err_INVALID_NAME') : t('pair.name_failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      data-testid="pair-name-step"
      onSubmit={(e) => {
        e.preventDefault();
        void save(name);
      }}
    >
      <p style={{ color: 'var(--ok)' }}>{t('pair.bound_name_title')}</p>
      <label className="field">
        <span>{t('pair.name_label')}</span>
        <input className="input" value={name} maxLength={DEVICE_NAME_MAX} onChange={(e) => setName(e.target.value)} disabled={busy || forbidden} autoFocus />
      </label>
      <p className="muted small">{t('pair.name_hint')}</p>
      {err && <p className="error">{err}</p>}
      {forbidden && <p className="muted">{t('pair.name_forbidden')}</p>}
      <div className="dialog-actions">
        {forbidden ? (
          <button type="button" className="btn btn-primary" onClick={() => done('')}>
            {t('common.done')}
          </button>
        ) : (
          <>
            <button type="button" className="btn" disabled={busy} onClick={() => void save(initial)}>
              {t('pair.name_skip')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {t('pair.name_save')}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
